/**
 * パイプラインで使うブラウザ実装 (DI のデフォルト値)。
 *
 * テスト時は上位 (run-all.ts の `deps`) にモックを差し込むため、
 * このモジュールは本番のブラウザ動作のみを担当する。
 */

import type { ClassificationResult, ImageMimeType } from '@shared/types';

import { removeReflectionForBlob } from '../api/cleanup-client';
import { classifyAll } from '../caption';
import {
  ImageWorkerPool,
  defaultBrowserWorkerFactory,
  type ProcessOptions,
  type ProcessedImage,
  type Result,
} from '../image';

export type CleanupBlobFn = (
  source: Blob,
) => Promise<{ blob: Blob; mimeType: ImageMimeType; processingTimeMs: number }>;

export async function defaultClassifyAll(
  files: readonly File[],
): Promise<readonly ClassificationResult[]> {
  return classifyAll(files, { concurrency: 10 });
}

/**
 * Wave 19: classifications を任意引数で受け取り、外装カテゴリで bbox があれば
 * smart crop 用 options に含めて Worker に渡す。内装は従来通り中央クロップ。
 */
export async function defaultProcessNonHero(
  files: readonly File[],
  onProgress: (completed: number) => void,
  classifications?: readonly ClassificationResult[],
): Promise<ReadonlyMap<number, Result<ProcessedImage>>> {
  const pool = new ImageWorkerPool(defaultBrowserWorkerFactory());
  try {
    return await runWorkerPool(files, pool, onProgress, classifications);
  } finally {
    pool.terminate();
  }
}

export const defaultCleanupBlob: CleanupBlobFn = (source) => removeReflectionForBlob(source);

// ===== 内部 ============================================================

async function runWorkerPool(
  files: readonly File[],
  pool: ImageWorkerPool,
  onProgress: (completed: number) => void,
  classifications?: readonly ClassificationResult[],
): Promise<ReadonlyMap<number, Result<ProcessedImage>>> {
  const map = new Map<number, Result<ProcessedImage>>();
  let completed = 0;
  await Promise.all(
    files.map(async (file, inputIdx) => {
      const buffer = await file.arrayBuffer();
      const options = buildProcessOptions(classifications?.[inputIdx]);
      const res = await pool.process({
        id: `job-${inputIdx}`,
        fileName: file.name,
        fileType: file.type,
        fileBuffer: buffer,
        options,
      });
      if (res.ok) {
        map.set(inputIdx, {
          ok: true,
          value: {
            blob: new Blob([res.outputBuffer], { type: res.mimeType }),
            width: res.width,
            height: res.height,
            mimeType: res.mimeType,
            processingTimeMs: res.processingTimeMs,
          },
        });
      } else {
        map.set(inputIdx, {
          ok: false,
          error: { code: 'UNSUPPORTED_FORMAT', message: res.error, file: file.name },
        });
      }
      completed++;
      onProgress(completed);
    }),
  );
  return map;
}

/**
 * classification から ProcessOptions を構築。外装 (2-5) + bbox あり →
 * smartCropBbox を設定。それ以外は空の options (従来の中央クロップ)。
 */
function buildProcessOptions(cls: ClassificationResult | undefined): ProcessOptions {
  if (!cls?.carBoundingBox) return {};
  if (cls.categoryId < 2 || cls.categoryId > 5) return {};
  const bbox = cls.carBoundingBox;
  return {
    smartCropBbox: { x: bbox.x, y: bbox.y, w: bbox.w, h: bbox.h },
  };
}
